import { useState } from 'react'
import { PRESALES_STEPS } from '../types'
import type { PresalesStepDef } from '../types'
import type { StepState, StepStatus } from '../utils/progressSteps'

// Per-step colour palette — one distinct colour per slice of the 11-step flow.
// Ordered left-to-right so the rainbow progresses through the phases.
export const STEP_COLORS = [
  '#6366f1', // 1. Blueprint              — indigo (first step)
  '#3b82f6', // 2. Access Sent            — blue
  '#06b6d4', // 3. Help Doc               — cyan
  '#14b8a6', // 4. P&P: Added             — teal
  '#22c55e', // 5. P&P: Discussed         — green
  '#84cc16', // 6. P&P: Standard          — lime
  '#eab308', // 7. Scope Started          — yellow
  '#f59e0b', // 8. SOW w/ Impl            — amber
  '#f97316', // 9. SOW Sent               — orange
  '#f43f5e', // 10. SOW Signed            — rose
  '#a855f7', // 11. Handover              — purple
] as const

// Visual language for state: we keep the step's own colour on all slices and
// use opacity to convey progress.
const STATE_OPACITY: Record<StepState, number> = {
  done: 1.0,
  pending: 0.22,
  na: 0.1,
}
const STATE_OPACITY_HOVER: Record<StepState, number> = {
  done: 1.0,
  pending: 0.55,
  na: 0.3,
}

// Tooltip/centre-text colour for each state (falls back to the step's colour).
const STATE_TEXT_COLOR: Record<StepState, string | null> = {
  done: null,       // use step colour
  pending: '#94a3b8', // slate-400
  na: '#6b7280',    // gray-500
}

const formatDate = (iso?: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
}

function arcPath(cx: number, cy: number, rOuter: number, rInner: number, startAngle: number, endAngle: number) {
  const largeArc = endAngle - startAngle > 180 ? 1 : 0
  const startOuter = polarToCartesian(cx, cy, rOuter, endAngle)
  const endOuter = polarToCartesian(cx, cy, rOuter, startAngle)
  const startInner = polarToCartesian(cx, cy, rInner, startAngle)
  const endInner = polarToCartesian(cx, cy, rInner, endAngle)
  return [
    'M', startOuter.x, startOuter.y,
    'A', rOuter, rOuter, 0, largeArc, 0, endOuter.x, endOuter.y,
    'L', startInner.x, startInner.y,
    'A', rInner, rInner, 0, largeArc, 1, endInner.x, endInner.y,
    'Z',
  ].join(' ')
}

export interface ProgressDonutProps {
  statuses: StepStatus[]
  /** Outer size in pixels. Inner ring and gap scale proportionally. */
  size?: number
  /**
   * Called when a user clicks a slice. Receives the step def and the computed
   * status. The caller is responsible for calling onToggleAction if appropriate.
   */
  onSliceClick?: (def: PresalesStepDef, status: StepStatus, index: number) => void
  /**
   * Called when the hovered slice changes. Pass-through for surfaces that want
   * to render their own context panel next to the donut. Null = nothing hovered.
   */
  onHoverChange?: (info: { def: PresalesStepDef; status: StepStatus; index: number } | null) => void
  /** Show the default centre text (percent / "n/n steps"). */
  showCentreText?: boolean
  /**
   * Style of centre text.
   *  - 'full': big percent + "n/n steps", and on hover swaps to step info. Good for large donuts.
   *  - 'compact': just "n/n" — fits inside a tiny donut.
   */
  centreMode?: 'full' | 'compact'
}

/**
 * Reusable donut chart visualising the 10-step Presales Progress flow.
 * Each slice gets a native <title> for browser-level hover tooltips so it works
 * even at tiny sizes (e.g. inline inside a dense table row).
 */
export function ProgressDonut({
  statuses,
  size = 220,
  onSliceClick,
  onHoverChange,
  showCentreText = true,
  centreMode = 'full',
}: ProgressDonutProps) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)

  const totalCount = PRESALES_STEPS.length
  const doneCount = statuses.filter((s) => s.state === 'done').length
  const percent = Math.round((doneCount / totalCount) * 100)

  const cx = size / 2
  const cy = size / 2
  // Scale ring thickness with size. Tiny donuts need a thicker ring ratio to stay legible.
  const ringRatio = size < 80 ? 0.55 : 0.42
  const rOuter = size * 0.45
  const rInner = rOuter * (1 - ringRatio)
  const gapDeg = size < 80 ? 1.8 : 1.2
  const sliceDeg = 360 / totalCount

  const handleHover = (idx: number | null) => {
    setHoverIdx(idx)
    if (onHoverChange) {
      if (idx == null) onHoverChange(null)
      else onHoverChange({ def: PRESALES_STEPS[idx], status: statuses[idx], index: idx })
    }
  }

  const hovered = hoverIdx != null ? { def: PRESALES_STEPS[hoverIdx], status: statuses[hoverIdx], index: hoverIdx } : null
  const hoveredClickable = hovered ? !!onSliceClick && hovered.status.toggleable : false

  return (
    <div className="relative inline-block" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label="Presales progress">
        {PRESALES_STEPS.map((def, idx) => {
          const s = statuses[idx]
          const start = idx * sliceDeg + gapDeg / 2
          const end = (idx + 1) * sliceDeg - gapDeg / 2
          const isHover = hoverIdx === idx
          const fill = STEP_COLORS[idx % STEP_COLORS.length]
          const opacity = isHover ? STATE_OPACITY_HOVER[s.state] : STATE_OPACITY[s.state]
          const clickable = !!onSliceClick && s.toggleable
          // Midpoint of the slice — where we render the letter for multi-action steps.
          const midAngle = (start + end) / 2
          const rMid = (rOuter + rInner) / 2
          const { x: letterX, y: letterY } = polarToCartesian(cx, cy, rMid, midAngle)
          return (
            <g key={def.id}>
              <path
                d={arcPath(cx, cy, rOuter, rInner, start, end)}
                fill={fill}
                fillOpacity={opacity}
                stroke={s.state === 'na' ? fill : 'transparent'}
                strokeWidth={s.state === 'na' ? 1 : 0}
                strokeDasharray={s.state === 'na' ? '3 2' : undefined}
                style={{
                  cursor: clickable ? 'pointer' : 'default',
                  transition: 'fill-opacity 140ms ease, transform 140ms ease',
                  transformOrigin: `${cx}px ${cy}px`,
                  transform: isHover ? 'scale(1.06)' : 'scale(1)',
                }}
                onMouseEnter={() => handleHover(idx)}
                onMouseLeave={() => handleHover(hoverIdx === idx ? null : hoverIdx)}
                onClick={(e) => {
                  e.stopPropagation()
                  onSliceClick?.(def, s, idx)
                }}
              />
              {s.abbrev && s.state === 'done' && (
                <text
                  x={letterX}
                  y={letterY}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={size < 100 ? 10 : 14}
                  fontWeight={700}
                  fill="white"
                  style={{ pointerEvents: 'none', textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}
                >
                  {s.abbrev}
                </text>
              )}
            </g>
          )
        })}
      </svg>

      {/* Floating popover tooltip — shown whenever the centre isn't rich enough to carry
          hover detail (i.e. no centre text at all, or compact-mode centre which only shows n/total).
          Positioned BELOW the donut so it isn't clipped by the table's overflow-x scroll region. */}
      {(!showCentreText || centreMode === 'compact') && hovered && (
        <div
          className="absolute z-50 pointer-events-none rounded-md shadow-xl px-3 py-2 text-left border border-white/10"
          style={{
            left: '50%',
            top: `calc(100% + 8px)`,
            transform: 'translateX(-50%)',
            backgroundColor: '#0f172a',
            color: 'white',
            minWidth: 200,
            maxWidth: 260,
          }}
        >
          <div className="text-xs font-semibold" style={{ color: STEP_COLORS[hovered.index % STEP_COLORS.length] }}>
            {hovered.index + 1}. {hovered.def.label}
          </div>
          <div className="text-[11px] opacity-80 mt-1 leading-snug">
            {hovered.def.description}
          </div>
          <div className="text-[11px] opacity-90 mt-2 font-medium">
            {hovered.status.state === 'done' && hovered.status.completedAt
              ? `✓ Completed ${formatDate(hovered.status.completedAt)}`
              : hovered.status.state === 'na'
                ? '∅ Not Required'
                : '○ Not yet started'}
          </div>
          {hovered.status.detail && (
            <div className="text-[10px] italic opacity-70 mt-0.5">{hovered.status.detail}</div>
          )}
          {hoveredClickable && (
            <div className="text-[9px] opacity-60 mt-1.5 uppercase tracking-wide">Click to toggle</div>
          )}
          {/* Pointer arrow — at the TOP of the tooltip, pointing up at the donut */}
          <div
            className="absolute h-2 w-2 border-l border-t border-white/10"
            style={{
              left: '50%',
              top: -5,
              transform: 'translateX(-50%) rotate(45deg)',
              backgroundColor: '#0f172a',
            }}
          />
        </div>
      )}

      {showCentreText && centreMode === 'full' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center px-2">
          {hoverIdx != null ? (
            <>
              <div
                className="text-[10px] uppercase tracking-wide"
                style={{ color: 'var(--text-muted)' }}
              >
                Step {hoverIdx + 1} of {totalCount}
              </div>
              <div
                className="text-sm font-semibold leading-tight"
                style={{ color: STATE_TEXT_COLOR[statuses[hoverIdx].state] ?? STEP_COLORS[hoverIdx % STEP_COLORS.length] }}
              >
                {PRESALES_STEPS[hoverIdx].label}
              </div>
              <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                {statuses[hoverIdx].state === 'done' && statuses[hoverIdx].completedAt
                  ? `Completed ${formatDate(statuses[hoverIdx].completedAt)}`
                  : statuses[hoverIdx].state === 'na'
                    ? 'Not Required'
                    : 'Not yet started'}
              </div>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{percent}%</div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{doneCount} / {totalCount} steps</div>
            </>
          )}
        </div>
      )}

      {/* Compact centre — always shows the fraction, suitable for tiny donuts. */}
      {showCentreText && centreMode === 'compact' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center">
          <div className="text-xs font-bold leading-none" style={{ color: 'var(--text-primary)' }}>
            {doneCount}<span style={{ color: 'var(--text-muted)' }}>/{totalCount}</span>
          </div>
        </div>
      )}
    </div>
  )
}

// Re-export for surfaces that need to render their own swatches matching a step.
export { STEP_COLORS as PROGRESS_STEP_COLORS }
